var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Maybe } from '@codegrade/maybe';
import { NonEmptyList } from '@codegrade/non-empty-list';
import { assert, defaultdict, expr, itertools } from '@codegrade/utils';
import { CacheDecorator } from '@/utils/cache';
/**
 * A quality comment.
 *
 * These can be used as linter comments and will be rendered in the gutter of the file viewer.
 *
 * These currently can be created with ATv1 and ATv2.
 */
export class QualityComment {
    /** @inheritdoc */
    autoTestV1StepId;
    /** @inheritdoc */
    fileId;
    /** @inheritdoc */
    severity;
    /** @inheritdoc */
    code;
    /** @inheritdoc */
    origin;
    /** @inheritdoc */
    msg;
    /** @inheritdoc */
    line;
    /** @inheritdoc */
    column;
    /** @param data The data to use for the new instance. */
    constructor(data) {
        this.autoTestV1StepId = data.autoTestV1StepId;
        this.fileId = data.fileId;
        this.severity = data.severity;
        this.code = data.code;
        this.origin = data.origin;
        this.msg = data.msg;
        this.line = data.line;
        this.column = data.column;
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data) {
        return new QualityComment({
            ...data,
            severity: Maybe.of(data.severity),
            autoTestV1StepId: Maybe.of(data.stepId),
        });
    }
    /**
     * Create a new QualityComment from a ATv2 comment that is connected to a file.
     *
     * @param data The comment to create the new `QualityComment` for.
     * @returns A fresh `QualityComment` object.
     */
    static fromATv2(data) {
        return new QualityComment({
            autoTestV1StepId: Maybe.empty(),
            fileId: data.file.id,
            code: data.code,
            msg: data.comment,
            origin: data.origin,
            severity: data.severity,
            line: { start: data.location.start.line, end: data.location.end.line },
            column: { start: data.location.start.column, end: data.location.end.column },
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static getBadgeVariant(severity) {
        switch (severity) {
            case 'fatal':
            case 'error':
                return 'danger';
            case 'warning':
            case 'info':
                return severity;
            default:
                return assert.AssertionError.assertNever(severity);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get badgeVariant() {
        return this.severity.map(QualityComment.getBadgeVariant);
    }
    /**
     * Get the correct gutter class to use for a collection of comments of a single line.
     *
     * @param comments The comments to get the class for.
     * @returns The class that should be used for the gutter for the line of the
     * given comments.
     */
    static getGutterClass(comments) {
        return NonEmptyList.fromArray(comments ?? [])
            .map(cs => new Set(itertools.filter(comment => comment.severity, cs)))
            .map(variants => {
            if (variants.has('fatal') || variants.has('error')) {
                return 'text-danger';
            }
            else if (variants.has('warning')) {
                return 'text-warning';
            }
            else {
                return 'text-info';
            }
        });
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class QualityComments {
    /** The cache storage used in this class. */
    _cache = new CacheDecorator();
    /** The comments stored in the class. */
    _comments;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(comments) {
        this._comments = Object.freeze(comments);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data) {
        const comments = data.map(QualityComment.fromServerData);
        return new QualityComments(comments);
    }
    /**
     * Create a QualityComments object from ATv2 results.
     *
     * @param autoTestComments The `autotest-comments module. This is given as
     * an argument so it doesn't need to be loaded by this module.
     * @param fileTree The file tree of the submission, used to connect the
     * comments to files.
     * @param runResult The ATv2 result to retrieve the comments for.
     * @returns A fresh QualityComments object with all the comments that can be
     * connected to files in the given result.
     */
    static fromATv2Result(autoTestComments, fileTree, runResult) {
        const comments = autoTestComments
            .extractComments(runResult.allStructuredOutput, resolvedPath => expr.switcher(resolvedPath.type, {
            absolute: () => Maybe.empty(),
            relative: () => fileTree.getByPath(resolvedPath.path),
        }))
            .map(QualityComment.fromATv2);
        return new QualityComments(comments);
    }
    /** @returns The comments per file id. */
    get commentsPerFile() {
        return itertools.groupBy(this._comments, comment => comment.fileId).toMaybeMap();
    }
    /** @returns The comments per AutoTest v1 step id. */
    get commentsPerStep() {
        const res = new defaultdict.DefaultMap(() => []);
        for (const comment of this._comments) {
            comment.autoTestV1StepId.ifJust(stepId => res.get(stepId).push(comment));
        }
        return res.toMaybeMap();
    }
}
__decorate([
    CacheDecorator.cache
], QualityComments.prototype, "commentsPerFile", null);
__decorate([
    CacheDecorator.cache
], QualityComments.prototype, "commentsPerStep", null);
